import { ContactsRowStateService } from '../../services/contacts-row-state.service';
import { ContactsWorkspaceStateService } from '../../services/contacts-workspace-state.service';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { BaseSidebarContainersItemComponent } from '@modules/common/components/base-sidebar-containers-item/base-sidebar-containers-item.component';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { SidebarSplitViewKey } from '@modules/common/types/sidebar-split-view-key';
import { GroupListStateService } from '@modules/contacts/services/group-list-state.service';
import { Contact } from '@modules/contacts/types/contact';
import { Group } from '@modules/contacts/types/group';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { VirtualFolder } from '@modules/contacts/types/virtual-folder';
import { StateKey } from '@modules/settings/types/state-key';
import { type HttpContext } from '@angular/common/http';
import { contactsOwnerContext } from '../../services/contacts-owner-context';
import { type ContactsRailScope } from '../sidebar-context-menu/contacts-rail-menu';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-side-bar-groups',
  templateUrl: './side-bar-groups.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-containers-item/base-sidebar-containers-item.component.less',
    './side-bar-groups.component.less',
  ],
  animations: [heightAnimation],
  standalone: false,
  providers: [
    ContactsRowStateService,
    { provide: ContactsWorkspaceStateService, useExisting: ContactsRowStateService },
    ...GroupListStateService.providers({
      stateKey: StateKey.sidebarGroupsListState,
    }),
  ],
})
export class SideBarGroupsComponent
  extends BaseSidebarContainersItemComponent<Group, Contact, GroupsFilters, VirtualFolder>
  implements OnInit, OnChanges
{
  application = Application.contacts;
  sidebarSplitViewKey = SidebarSplitViewKey.contacts;
  @Input() public tenantId: string | null | undefined;
  @Input() public title = 'Groups';
  @Input() public depth = 0;
  /** A workspace section of the rail: the header gets the prototype menu, an options button and a semantic plus. */
  @Input() public rail = false;
  /** Which workspace the section is: the member's My Workspace or the Team Workspace. */
  @Input() public railScope: ContactsRailScope = 'mine';
  private readonly rowState = inject(ContactsRowStateService, { optional: true });
  private readonly drafts = new Map<string, Group>();

  protected get railWorkspace(): string {
    return this.railScope === 'team' ? 'Team' : 'My';
  }

  /** The workspace this header's menus and forms act in: the section's own tenant, else the team's. */
  protected get railTenantId(): string | null | undefined {
    return this.tenantId === undefined ? this.team?.id : this.tenantId;
  }

  protected groupDraft(parentId?: string): Group {
    const owner = this.tenantId === undefined ? this.team?.id : this.tenantId;
    const key = JSON.stringify([owner, parentId]);
    if (!this.drafts.has(key)) this.drafts.set(key, new Group({ accountId: owner, parentId }));
    return this.drafts.get(key);
  }

  protected override railRequestContext(): HttpContext | undefined {
    return contactsOwnerContext(undefined, this.tenantId === undefined ? this.team?.id : this.tenantId);
  }

  private readonly groupListStateService = inject(GroupListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  ngOnChanges(changes: SimpleChanges): void {
    if ('tenantId' in changes || 'team' in changes) this.bindRowState();
  }

  private bindRowState(): void {
    if (!this.rowState) return;
    this.rowState.bindOwner(this.tenantId === undefined ? (this.team?.id ?? null) : this.tenantId);
    this.groupListStateService.state.restoreSavedState();
    this.groupListStateService.state.notifyStateChanged();
  }

  ngOnInit() {
    this.bindRowState();
    super.ngOnInit();

    combineLatest([this.groupListStateService.value(), this.displayTimeZoneService.resolvedTimeZone$])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState reads it to reckon its day windows in the account
        // zone (SBUX-829).
        this.filters = new GroupsFilters({ displayTimeZone }).applyListState(state);
      });
  }
}
